import React, { useEffect, useState } from 'react';
import API from 'shared/api';
import {
  FiPackage,
  FiRefreshCw,
  FiSearch,
  FiX,
  FiArrowUp,
  FiArrowDown,
  FiAlertTriangle,
  FiArchive,
  FiClock,
  FiDownload,
} from 'react-icons/fi';
import { COL } from 'shared/darkColors';
import { BarsH, LineChart } from 'shared/components/Dashboard/charts';
import {
  S, money, intFmt, Kpi, SectionCard, Cargando, ErrorMsg, buildQuery, descargarCSV,
} from './reportesCommon';

const TIPO_LABEL = {
  venta: 'Venta',
  devolucion: 'Devolución',
  produccion: 'Producción',
  anulacion: 'Anulación',
  carga: 'Carga',
  descarga: 'Descarga',
  ajuste: 'Ajuste',
  compra: 'Compra',
  entrada: 'Entrada',
  salida: 'Salida',
  conteo: 'Conteo',
  merma: 'Merma',
  consumo: 'Consumo',
  inventario_inicial: 'Inventario inicial',
  transferencia: 'Transferencia',
};

const TIPO_ES_SALIDA = new Set(['venta', 'descarga', 'salida', 'consumo', 'merma']);
const TIPO_ES_ENTRADA = new Set(['devolucion', 'carga', 'produccion', 'compra', 'entrada', 'inventario_inicial']);

function tipoColor(tipo) {
  if (TIPO_ES_SALIDA.has(tipo)) return COL.red;
  if (TIPO_ES_ENTRADA.has(tipo)) return COL.green;
  return COL.pri2;
}

function tipoLabel(tipo) {
  if (!tipo) return '—';
  return TIPO_LABEL[tipo] || tipo.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

const TABS = [
  { id: 'valorizacion', label: 'Valorización' },
  { id: 'rotacion', label: 'Rotación' },
  { id: 'resumen', label: 'Entradas/Salidas' },
  { id: 'movimientos', label: 'Movimientos' },
  { id: 'alertas', label: 'Alertas' },
  { id: 'stock-muerto', label: 'Stock muerto' },
  { id: 'historial', label: 'Historial precios' },
];

export default function ReporteInventario({ filtros }) {
  const [tab, setTab] = useState('valorizacion');
  const [valor, setValor] = useState(null);
  const [rotacion, setRotacion] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [alertas, setAlertas] = useState(null);
  const [stockMuerto, setStockMuerto] = useState(null);
  const [diasMuerto, setDiasMuerto] = useState(30);

  // Movimientos
  const [movs, setMovs] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [ultimoId, setUltimoId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [tipo, setTipo] = useState('');
  const [q, setQ] = useState('');
  const [usuarioCedula, setUsuarioCedula] = useState('');
  const [tiposOpciones, setTiposOpciones] = useState([]);
  const [usuariosOpciones, setUsuariosOpciones] = useState([]);
  const [detalle, setDetalle] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [referencia, setReferencia] = useState(null);

  // Historial de precios
  const [histQ, setHistQ] = useState('');
  const [histResultados, setHistResultados] = useState([]);
  const [histSeleccion, setHistSeleccion] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  // Valorización
  useEffect(() => {
    if (tab !== 'valorizacion') return;
    let alive = true;
    setLoading(true);
    setError(false);
    API.get(`/reportes/inventario/valorizacion?${buildQuery(filtros).toString()}`)
      .then((r) => alive && r.data.status === 'success' && setValor(r.data.data))
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filtros.sede, filtros.fechaDesde, filtros.fechaHasta]);

  // Rotación
  useEffect(() => {
    if (tab !== 'rotacion') return;
    let alive = true;
    setLoading(true);
    setError(false);
    API.get(`/reportes/inventario/rotacion?${buildQuery(filtros).toString()}`)
      .then((r) => alive && r.data.status === 'success' && setRotacion(r.data.data))
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filtros.sede, filtros.fechaDesde, filtros.fechaHasta]);

  // Resumen entradas/salidas
  useEffect(() => {
    if (tab !== 'resumen') return;
    let alive = true;
    setLoading(true);
    setError(false);
    API.get(`/reportes/inventario/movimientos/resumen?${buildQuery(filtros).toString()}`)
      .then((r) => alive && r.data.status === 'success' && setResumen(r.data.data))
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filtros.sede, filtros.fechaDesde, filtros.fechaHasta]);

  // Alertas
  useEffect(() => {
    if (tab !== 'alertas') return;
    let alive = true;
    setLoading(true);
    setError(false);
    API.get(`/inventario/alertas-stock?sede=${encodeURIComponent(filtros.sede || 'todas')}`)
      .then((r) => alive && r.data.status === 'success' && setAlertas(r.data.data))
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filtros.sede]);

  // Stock muerto
  useEffect(() => {
    if (tab !== 'stock-muerto') return;
    let alive = true;
    setLoading(true);
    setError(false);
    API.get(`/reportes/inventario/stock-muerto?${buildQuery(filtros, { dias: diasMuerto }).toString()}`)
      .then((r) => alive && r.data.status === 'success' && setStockMuerto(r.data.data))
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filtros.sede, diasMuerto]);

  // Filtros de movimientos
  useEffect(() => {
    if (tab !== 'movimientos') return;
    API.get('/reportes/inventario/movimientos/filtros')
      .then((r) => {
        if (r.data.status === 'success') {
          setTiposOpciones(r.data.data.tipos || []);
          setUsuariosOpciones(r.data.data.usuarios || []);
        }
      })
      .catch(() => {});
  }, [tab]);

  useEffect(() => {
    if (tab !== 'movimientos') return;
    setMovs([]);
    setUltimoId(null);
    setHasMore(false);
    cargarMovs(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filtros.sede, filtros.fechaDesde, filtros.fechaHasta, tipo, q, usuarioCedula]);

  const cargarMovs = async (reset) => {
    setLoading(true);
    setError(false);
    try {
      const p = buildQuery(filtros);
      p.set('limite', '50');
      if (tipo) p.set('tipo', tipo);
      if (usuarioCedula) p.set('usuario_cedula', usuarioCedula);
      if (q.trim()) p.set('q', q.trim());
      if (!reset && ultimoId) p.set('ultimo_id', ultimoId);
      const r = await API.get(`/reportes/inventario/movimientos?${p.toString()}`);
      if (r.data.status === 'success') {
        const d = r.data.data;
        setMovs((prev) => (reset ? d.movimientos : [...prev, ...d.movimientos]));
        setHasMore(d.has_more);
        if (d.has_more) setUltimoId(d.ultimo_id);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const abrirDetalle = async (id) => {
    setDetalleLoading(true);
    setDetalle(null);
    setReferencia(null);
    try {
      const r = await API.get(`/reportes/inventario/movimiento/${id}`);
      if (r.data.status === 'success') {
        setDetalle(r.data.data);
        // Resolver referencia (venta/pedido/produccion) si existe.
        if (r.data.data.referencia_id) {
          API.get(`/reportes/inventario/movimientos/${id}/referencia`)
            .then((rr) => rr.data.status === 'success' && setReferencia(rr.data.data))
            .catch(() => {});
        }
      }
    } catch {
    } finally {
      setDetalleLoading(false);
    }
  };

  const buscarHistorial = async (query) => {
    if (!query || !query.trim()) {
      setHistResultados([]);
      return;
    }
    const r = await API.get(`/reportes/inventario/productos/buscar?q=${encodeURIComponent(query.trim())}`);
    if (r.data.status === 'success') setHistResultados(r.data.data.productos || []);
  };

  const seleccionarProductoHist = async (p) => {
    setHistSeleccion(p);
    setHistLoading(true);
    try {
      const r = await API.get(`/inventario/historial-precios/${encodeURIComponent(p.codigo)}`);
      if (r.data.status === 'success') setHistorial(r.data.data.historial || []);
    } catch {
      setHistorial([]);
    } finally {
      setHistLoading(false);
    }
  };

  const exportarExcel = async () => {
    try {
      const r = await API.get(`/reportes/inventario/exportar?${buildQuery(filtros).toString()}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'reporte_inventario.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {}
  };

  const exportarCSV = () => {
    if (tab === 'movimientos') {
      descargarCSV(
        'reporte_movimientos.csv',
        ['ID', 'Fecha', 'Sede', 'Motivo', 'Referencia', 'Artículos', 'Cantidad', 'Usuario'],
        movs.map((m) => [
          m.id, m.fecha, m.sede_codigo, tipoLabel(m.tipo),
          `${m.referencia_tipo || ''}${m.referencia_id ? ' #' + m.referencia_id : ''}`,
          (m.items || []).map((i) => `${i.nombre} (${i.cantidad})`).join(' | '),
          m.cantidad_total, m.usuario_nombre || m.usuario_cedula || '—',
        ])
      );
    } else if (tab === 'rotacion' && rotacion) {
      descargarCSV(
        'reporte_rotacion.csv',
        ['Producto', 'Código', 'Departamento', 'Vendido', 'Ingresos', 'Stock', 'Ratio'],
        rotacion.productos.map((p) => [p.nombre, p.codigo, p.departamento, p.vendido, p.ingresos, p.stock, p.ratio ?? ''])
      );
    } else if (tab === 'alertas' && alertas) {
      descargarCSV(
        'reporte_alertas_stock.csv',
        ['Producto', 'Código', 'Stock', 'Departamento', 'Alerta'],
        [...(alertas.agotados || []), ...(alertas.bajo_stock || [])].map((a) => [a.descripcion, a.codigo, a.stock, a.departamento, a.alerta])
      );
    }
  };

  const cargandoVista = loading && ['valorizacion', 'rotacion', 'resumen', 'alertas', 'stock-muerto'].includes(tab);
  const errorVista = error && ['valorizacion', 'rotacion', 'resumen', 'alertas', 'stock-muerto'].includes(tab);

  if (cargandoVista) return <Cargando />;
  if (errorVista) return <ErrorMsg />;

  const hayFiltrosMov = !!(tipo || q.trim() || usuarioCedula);

  const exportDisabled = !(
    (tab === 'movimientos' && movs.length) ||
    (tab === 'rotacion' && rotacion?.productos?.length) ||
    (tab === 'alertas' && alertas)
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '14px' }}>
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ ...S.btn, ...(tab === t.id ? S.btnActive : {}) }}>
            {t.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button onClick={exportarCSV} disabled={exportDisabled} style={{ ...S.btn, opacity: exportDisabled ? 0.5 : 1 }}>
          CSV
        </button>
        <button onClick={exportarExcel} style={{ ...S.btn, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <FiDownload size={12} /> Excel
        </button>
      </div>

      {tab === 'valorizacion' && valor && (
        <>
          <div style={S.kpiGrid}>
            <Kpi label="Valor (costo)" value={money(valor.valor_total)} color={COL.pri} />
            <Kpi label="Valor (precio venta)" value={money(valor.valor_precio_total)} color={COL.pri2} />
            <Kpi label="Margen potencial" value={money(valor.margen_potencial)} color={COL.green} />
            <Kpi label="Unidades disponibles" value={intFmt(valor.unidades_total)} />
            <Kpi label="Productos" value={intFmt(valor.productos)} />
            <Kpi label="Venta diaria" value={money(valor.venta_diaria)} sub="promedio del período" />
            <Kpi
              label="Días de inventario"
              value={valor.dias_inventario != null ? `${valor.dias_inventario} días` : '—'}
              sub="valor ÷ venta diaria"
              color={valor.dias_inventario != null && valor.dias_inventario > 60 ? COL.amber : COL.pri}
            />
          </div>
          <div style={S.cardGrid}>
            <SectionCard title="Valor por sede (costo)" icon={FiPackage} color={COL.pri2}>
              <BarsH items={valor.por_sede.map((s) => ({ label: s.sede, value: s.valor, sub: `${intFmt(s.unidades)} unds` }))} fmt={money} color={COL.pri2} />
            </SectionCard>
            <SectionCard title="Valor por departamento (costo)" icon={FiPackage} color={COL.purple}>
              <BarsH items={valor.por_departamento.map((d) => ({ label: d.departamento, value: d.valor, sub: `${intFmt(d.unidades)} unds` }))} fmt={money} color={COL.purple} />
            </SectionCard>
          </div>
        </>
      )}

      {tab === 'rotacion' && rotacion && (
        <SectionCard title="Rotación: más vendidos vs stock actual" icon={FiRefreshCw} color={COL.pri2}>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>{['Producto', 'Departamento', 'Vendido', 'Ingresos', 'Stock', 'Ratio (vendido/stock)'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {rotacion.productos.map((p) => (
                  <tr key={p.codigo}>
                    <td style={S.td}>
                      <div style={{ fontWeight: 600 }}>{p.nombre}</div>
                      <div style={{ color: COL.muted, fontSize: '10px' }}>{p.codigo}</div>
                    </td>
                    <td style={{ ...S.td, color: COL.muted }}>{p.departamento || '—'}</td>
                    <td style={S.td}>{intFmt(p.vendido)}</td>
                    <td style={{ ...S.td, color: COL.green }}>{money(p.ingresos)}</td>
                    <td style={S.td}>{intFmt(p.stock)}</td>
                    <td style={S.td}>{p.ratio == null ? '—' : p.ratio.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rotacion.productos.length === 0 && <div style={{ textAlign: 'center', padding: '20px', color: COL.muted }}>Sin ventas en el período</div>}
        </SectionCard>
      )}

      {tab === 'resumen' && resumen && (
        <>
          <div style={S.kpiGrid}>
            <Kpi label="Entradas" value={intFmt(resumen.entradas)} color={COL.green} />
            <Kpi label="Salidas" value={intFmt(resumen.salidas)} color={COL.red} />
            <Kpi label="Neto" value={intFmt(resumen.neto)} color={resumen.neto >= 0 ? COL.green : COL.red} />
          </div>
          <div style={S.cardGrid}>
            <SectionCard title="Entradas y salidas por motivo" icon={FiPackage} color={COL.pri2}>
              <BarsH
                items={resumen.por_tipo.map((t) => ({
                  label: tipoLabel(t.tipo),
                  value: t.entradas + t.salidas,
                  sub: `+${intFmt(t.entradas)} / -${intFmt(t.salidas)} en ${intFmt(t.lineas)} líneas`,
                }))}
                fmt={intFmt}
                color={COL.pri2}
              />
            </SectionCard>
            <SectionCard title="Flujo neto por día" icon={FiRefreshCw} color={COL.purple}>
              <LineChart
                series={[{ name: 'Neto', values: resumen.flujo_por_dia.map((f) => f.neto), color: COL.pri2, area: true }]}
                labels={resumen.flujo_por_dia.map((f) => f.dia.slice(5))}
                fmt={intFmt}
              />
            </SectionCard>
          </div>
        </>
      )}

      {tab === 'movimientos' && (
        <SectionCard
          title="Movimientos de stock"
          icon={FiPackage}
          color={COL.pri}
          hint="Auditoría completa: qué artículo se movió, por qué, quién y su detalle de stock"
        >
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
              <FiSearch size={13} color={COL.muted} style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)' }} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar artículo (código o nombre)…"
                style={{ ...S.input, width: '100%', paddingLeft: '28px', boxSizing: 'border-box' }}
              />
            </div>
            <select value={tipo} onChange={(e) => setTipo(e.target.value)} style={S.select}>
              <option value="">Todos los motivos</option>
              {tiposOpciones.map((t) => (
                <option key={t.tipo} value={t.tipo}>{tipoLabel(t.tipo)} ({t.total})</option>
              ))}
            </select>
            <select value={usuarioCedula} onChange={(e) => setUsuarioCedula(e.target.value)} style={S.select}>
              <option value="">Todos los usuarios</option>
              {usuariosOpciones.map((u) => (
                <option key={u.cedula} value={u.cedula}>{u.nombre}</option>
              ))}
            </select>
            {hayFiltrosMov && (
              <button onClick={() => { setTipo(''); setQ(''); setUsuarioCedula(''); }} style={{ ...S.btn, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <FiX size={12} /> Limpiar
              </button>
            )}
          </div>

          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>{['Fecha', 'Sede', 'Motivo', 'Referencia', 'Artículos', 'Usuario'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {movs.map((m) => (
                  <tr key={m.id} onClick={() => abrirDetalle(m.id)} style={{ cursor: 'pointer' }} title="Ver detalle">
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                      <div>{m.fecha?.slice(0, 10)}</div>
                      <div style={{ color: COL.muted, fontSize: '10px' }}>{m.fecha?.slice(11, 16)}</div>
                    </td>
                    <td style={{ ...S.td, color: COL.muted }}>{m.sede_codigo}</td>
                    <td style={S.td}>
                      <span style={{ ...S.chip, color: tipoColor(m.tipo), background: COL.bg }}>{tipoLabel(m.tipo)}</span>
                    </td>
                    <td style={{ ...S.td, color: COL.pri2, fontWeight: 600 }}>
                      {m.referencia_tipo ? `${m.referencia_tipo.replace(/_/g, ' ')} #${m.referencia_id ?? ''}` : '—'}
                    </td>
                    <td style={S.td}>
                      <div style={{ maxWidth: '260px' }}>
                        {(m.items || []).slice(0, 3).map((i) => (
                          <div key={i.codigo} style={{ fontSize: '11px', display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.nombre}</span>
                            <span style={{ color: COL.muted, flexShrink: 0 }}>{intFmt(i.cantidad)}</span>
                          </div>
                        ))}
                        {m.lineas > 3 && <div style={{ color: COL.muted, fontSize: '10px' }}>+{m.lineas - 3} más…</div>}
                      </div>
                    </td>
                    <td style={{ ...S.td, color: COL.muted }}>{m.usuario_nombre || m.usuario_cedula || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {movs.length === 0 && !loading && <div style={{ textAlign: 'center', padding: '24px', color: COL.muted }}>Sin movimientos para los filtros seleccionados</div>}
          {hasMore && (
            <div style={{ textAlign: 'center', marginTop: '10px' }}>
              <button onClick={() => cargarMovs(false)} disabled={loading} style={S.btn}>
                {loading ? 'Cargando…' : 'Cargar más'}
              </button>
            </div>
          )}
        </SectionCard>
      )}

      {tab === 'alertas' && alertas && (
        <>
          <div style={S.kpiGrid}>
            <Kpi label="Agotados / negativos" value={intFmt(alertas.total_agotados)} color={COL.red} />
            <Kpi label="Bajo stock" value={intFmt(alertas.total_bajo_stock)} color={COL.amber} />
            <Kpi label="Total alertas" value={intFmt(alertas.total_alertas)} />
          </div>
          <SectionCard title="Alertas de stock" icon={FiAlertTriangle} color={COL.red}>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>{['Producto', 'Departamento', 'Stock', 'Alerta'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {[...(alertas.agotados || []), ...(alertas.bajo_stock || [])].map((a) => (
                    <tr key={a.codigo}>
                      <td style={S.td}>
                        <div style={{ fontWeight: 600 }}>{a.descripcion}</div>
                        <div style={{ color: COL.muted, fontSize: '10px' }}>{a.codigo}</div>
                      </td>
                      <td style={{ ...S.td, color: COL.muted }}>{a.departamento || '—'}</td>
                      <td style={{ ...S.td, fontWeight: 700 }}>{intFmt(a.stock)}</td>
                      <td style={S.td}>
                        <span style={{ ...S.chip, color: a.alerta === 'negativo' || a.alerta === 'agotado' ? COL.red : COL.amber, background: COL.bg }}>
                          {a.alerta === 'negativo' ? 'Negativo' : a.alerta === 'agotado' ? 'Agotado' : 'Bajo stock'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {[...(alertas.agotados || []), ...(alertas.bajo_stock || [])].length === 0 && (
              <div style={{ textAlign: 'center', padding: '24px', color: COL.muted }}>Sin alertas de stock</div>
            )}
          </SectionCard>
        </>
      )}

      {tab === 'stock-muerto' && stockMuerto && (
        <>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
            <span style={{ fontSize: '12px', color: COL.muted, fontWeight: 600 }}>Sin ventas en los últimos</span>
            <select value={diasMuerto} onChange={(e) => setDiasMuerto(Number(e.target.value))} style={S.select}>
              <option value={30}>30 días</option>
              <option value={60}>60 días</option>
              <option value={90}>90 días</option>
              <option value={180}>180 días</option>
            </select>
          </div>
          <div style={S.kpiGrid}>
            <Kpi label="Valor congelado" value={money(stockMuerto.valor_total)} color={COL.amber} />
            <Kpi label="Productos sin rotación" value={intFmt(stockMuerto.productos.length)} />
          </div>
          <SectionCard title="Stock muerto (con stock, sin ventas)" icon={FiArchive} color={COL.amber}>
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>{['Producto', 'Departamento', 'Disponible', 'Costo unit.', 'Valor'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {stockMuerto.productos.map((p) => (
                    <tr key={p.codigo}>
                      <td style={S.td}>
                        <div style={{ fontWeight: 600 }}>{p.nombre}</div>
                        <div style={{ color: COL.muted, fontSize: '10px' }}>{p.codigo}</div>
                      </td>
                      <td style={{ ...S.td, color: COL.muted }}>{p.departamento || '—'}</td>
                      <td style={S.td}>{intFmt(p.disponible)}</td>
                      <td style={S.td}>{money(p.costo_unitario)}</td>
                      <td style={{ ...S.td, fontWeight: 700, color: COL.amber }}>{money(p.valor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {stockMuerto.productos.length === 0 && <div style={{ textAlign: 'center', padding: '24px', color: COL.muted }}>Sin stock muerto en el período</div>}
          </SectionCard>
        </>
      )}

      {tab === 'historial' && (
        <SectionCard title="Historial de precios" icon={FiClock} color={COL.pri} hint="Cambios de precio y costo de un artículo">
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ position: 'relative', flex: '1', minWidth: '220px' }}>
              <FiSearch size={13} color={COL.muted} style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)' }} />
              <input
                value={histQ}
                onChange={(e) => { setHistQ(e.target.value); buscarHistorial(e.target.value); }}
                placeholder="Buscar producto por código o nombre…"
                style={{ ...S.input, width: '100%', paddingLeft: '28px', boxSizing: 'border-box' }}
              />
            </div>
            {histSeleccion && (
              <button onClick={() => { setHistSeleccion(null); setHistorial([]); setHistQ(''); setHistResultados([]); }} style={{ ...S.btn, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <FiX size={12} /> Limpiar
              </button>
            )}
          </div>

          {!histSeleccion && histResultados.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              {histResultados.map((p) => (
                <button key={p.codigo} onClick={() => seleccionarProductoHist(p)} style={{ ...S.btn, margin: '0 6px 6px 0' }}>
                  {p.nombre} <span style={{ color: COL.muted }}>({p.codigo})</span>
                </button>
              ))}
            </div>
          )}

          {histSeleccion && (
            <>
              <div style={{ fontSize: '13px', fontWeight: 700, color: COL.pri, marginBottom: '10px' }}>
                {histSeleccion.nombre} <span style={{ color: COL.muted, fontWeight: 600 }}>({histSeleccion.codigo})</span>
              </div>
              {histLoading ? <Cargando /> : (
                <div style={S.tableWrap}>
                  <table style={S.table}>
                    <thead>
                      <tr>{['Fecha', 'Precio ant.', 'Precio nuevo', 'Costo ant.', 'Costo nuevo', 'Usuario'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {historial.map((h) => (
                        <tr key={h.id}>
                          <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{String(h.fecha || '').slice(0, 16).replace('T', ' ')}</td>
                          <td style={S.td}>{money(h.precio_anterior)}</td>
                          <td style={{ ...S.td, fontWeight: 700, color: COL.pri2 }}>{money(h.precio_nuevo)}</td>
                          <td style={S.td}>{money(h.costo_anterior)}</td>
                          <td style={{ ...S.td, fontWeight: 600 }}>{money(h.costo_nuevo)}</td>
                          <td style={{ ...S.td, color: COL.muted }}>{h.usuario_nombre || h.usuario_cedula || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!histLoading && historial.length === 0 && <div style={{ textAlign: 'center', padding: '24px', color: COL.muted }}>Sin cambios de precio registrados</div>}
            </>
          )}
        </SectionCard>
      )}

      {detalle && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(16,24,40,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}
          onClick={() => setDetalle(null)}>
          <div style={{ background: COL.white, borderRadius: '12px', maxWidth: '680px', width: '100%', maxHeight: '86vh', overflowY: 'auto', padding: '16px', boxShadow: '0 12px 32px rgba(16,24,40,0.25)' }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '15px', fontWeight: 800, color: COL.pri }}>Movimiento #{detalle.id}</span>
                <span style={{ ...S.chip, color: tipoColor(detalle.tipo), background: COL.bg }}>{tipoLabel(detalle.tipo)}</span>
              </div>
              <button onClick={() => setDetalle(null)} style={{ ...S.btn, padding: '4px 8px' }}><FiX size={12} style={{ verticalAlign: '-2px' }} /> Cerrar</button>
            </div>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px', fontSize: '12px', color: COL.muted }}>
              <span>{detalle.fecha?.slice(0, 16).replace('T', ' ')}</span>
              <span>· {detalle.sede_nombre || detalle.sede_codigo}</span>
              <span>· {detalle.usuario_nombre || detalle.usuario_cedula || 'Sistema'}</span>
            </div>

            {referencia && (
              <div style={{ background: COL.bg, borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', fontSize: '12px' }}>
                <div style={{ fontWeight: 700, color: COL.pri, marginBottom: '6px' }}>
                  {referencia.tipo.replace(/_/g, ' ')} #{referencia.id}
                </div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', color: COL.muted }}>
                  {referencia.total != null && <span>Total: <b style={{ color: COL.pri }}>{money(referencia.total)}</b></span>}
                  {referencia.cliente_nombre && <span>Cliente: {referencia.cliente_nombre}</span>}
                  {referencia.estado && <span>Estado: {referencia.estado}</span>}
                  {referencia.receta_nombre && <span>Receta: {referencia.receta_nombre}</span>}
                  {referencia.cantidad != null && <span>Cantidad: {intFmt(referencia.cantidad)}</span>}
                  {referencia.metodo_pago && <span>Método: {referencia.metodo_pago.replace(/_/g, ' ')}</span>}
                  {referencia.ticket_numero != null && <span>Ticket: {referencia.ticket_numero}</span>}
                </div>
              </div>
            )}

            {detalle.notas && (
              <div style={{ background: COL.bg, borderRadius: '8px', padding: '8px 10px', fontSize: '12px', marginBottom: '12px' }}>{detalle.notas}</div>
            )}

            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>{['Artículo', 'Departamento', 'Cantidad', 'Stock anterior', 'Stock nuevo'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {detalle.items.map((it) => {
                    const delta = it.stock_nuevo != null && it.stock_anterior != null ? it.stock_nuevo - it.stock_anterior : null;
                    const salida = delta != null && delta < 0;
                    return (
                      <tr key={it.id}>
                        <td style={S.td}>
                          <div style={{ fontWeight: 600 }}>{it.nombre}</div>
                          <div style={{ color: COL.muted, fontSize: '10px' }}>{it.codigo}</div>
                        </td>
                        <td style={{ ...S.td, color: COL.muted }}>{it.departamento || '—'}</td>
                        <td style={{ ...S.td, fontWeight: 700, color: salida ? COL.red : COL.green }}>
                          {delta != null && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                              {salida ? <FiArrowDown size={12} /> : <FiArrowUp size={12} />}
                              {intFmt(Math.abs(delta))}
                            </span>
                          )}
                        </td>
                        <td style={{ ...S.td, color: COL.muted }}>{it.stock_anterior != null ? intFmt(it.stock_anterior) : '—'}</td>
                        <td style={{ ...S.td, fontWeight: 600 }}>{it.stock_nuevo != null ? intFmt(it.stock_nuevo) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {detalle.items.length === 0 && <div style={{ textAlign: 'center', padding: '20px', color: COL.muted }}>Sin items registrados</div>}
          </div>
        </div>
      )}
      {detalleLoading && <div style={{ textAlign: 'center', padding: '12px', color: COL.muted }}>Cargando detalle…</div>}
    </div>
  );
}
